import { Link } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tag, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';

const PaymentSection = ({
  bookingId,
  classData,
  discountCode,
  setDiscountCode,
  appliedDiscount,
  setAppliedDiscount,
  validatingDiscount,
  setValidatingDiscount,
  onPaymentSuccess,
  onPayByCash,
  onCancel
}) => {
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    
    setValidatingDiscount(true);
    try {
      const response = await apiClient.post('/discount-codes/validate', {
        code: discountCode.trim(),
        class_id: classData.class_id
      });
      setAppliedDiscount(response.data);
      toast.success(`Discount applied: ${response.data.discount_type === 'percentage' 
        ? `${response.data.discount_value}% off` 
        : `£${response.data.discount_amount} off`}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setAppliedDiscount(null);
    toast.info('Discount removed');
  };

  return (
    <div className="space-y-4">
      <div className="bg-background-paper border border-primary/20 rounded-sm p-4">
        <p className="text-sm text-neutral-300 mb-2">Booking created! Complete payment below:</p>
        <p className="text-xs text-neutral-500">Booking ID: {bookingId}</p>
      </div>

      {/* Discount Code Input */}
      <div className="bg-background border border-white/10 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Have a discount code?</span>
        </div>
        {appliedDiscount ? (
          <div className="flex items-center justify-between bg-success/10 border border-success/30 rounded-sm p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="font-mono font-bold">{appliedDiscount.code}</span>
              <span className="text-sm text-success">
                ({appliedDiscount.discount_type === 'percentage' 
                  ? `${appliedDiscount.discount_value}% off` 
                  : `£${appliedDiscount.discount_amount} off`})
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={removeDiscount}
              className="text-error hover:text-error"
              data-testid="remove-discount-btn"
            >
              Remove
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Enter code"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              className="bg-background border-white/10 font-mono"
              data-testid="discount-code-input"
            />
            <Button
              onClick={validateDiscountCode}
              disabled={validatingDiscount}
              variant="outline"
              data-testid="apply-discount-btn"
            >
              {validatingDiscount ? '...' : 'Apply'}
            </Button>
          </div>
        )}
      </div>

      {/* Price Summary */}
      {appliedDiscount && (
        <div className="bg-success/10 border border-success/30 rounded-sm p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-neutral-400">Original Price</span>
            <span>£{appliedDiscount.original_price}</span>
          </div>
          <div className="flex justify-between text-sm mb-2 text-success">
            <span>Discount</span>
            <span>-£{appliedDiscount.discount_amount}</span>
          </div>
          <div className="flex justify-between font-bold text-lg border-t border-success/30 pt-2 mt-2">
            <span>Total</span>
            <span className="text-success">£{appliedDiscount.final_price}</span>
          </div>
        </div>
      )}
      
      <PayPalScriptProvider options={{ 
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
        currency: "GBP",
        intent: "capture"
      }}>
        <PayPalButtons
          forceReRender={[bookingId, classData?.price, appliedDiscount?.final_price]}
          createOrder={async () => {
            try {
              const discountParam = appliedDiscount ? `&discount_code=${appliedDiscount.code}` : '';
              const response = await apiClient.post(`/payments/create-order?booking_id=${bookingId}${discountParam}`);
              return response.data.order_id;
            } catch (error) {
              toast.error('Failed to create payment order');
              throw error;
            }
          }}
          onApprove={async (data) => {
            try {
              await apiClient.post(`/payments/capture-order?order_id=${data.orderID}`);
              onPaymentSuccess(appliedDiscount?.final_price || classData?.price);
              toast.success('Payment completed! Confirmation email sent.');
            } catch (error) {
              toast.error('Payment capture issue - please contact support if payment was taken.');
            }
          }}
          onCancel={() => {
            toast.info('Payment cancelled. You can try again or pay by cash.');
          }}
          onError={() => {
            toast.error('Payment failed. Please try "Pay by Cash" option.');
          }}
          style={{
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'pay'
          }}
        />
      </PayPalScriptProvider>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-white/20" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-neutral-500">Or</span>
        </div>
      </div>

      <Button
        className="w-full bg-success hover:bg-success/80 font-bold py-4"
        onClick={onPayByCash}
        data-testid="pay-cash-btn"
      >
        PAY BY CASH (At Reception)
      </Button>
      <p className="text-xs text-neutral-500 text-center mt-2">
        Your booking will be confirmed immediately. Pay at the reception desk before class.
      </p>

      <Button
        variant="outline"
        className="w-full mt-4"
        onClick={onCancel}
        data-testid="skip-payment-btn"
      >
        CANCEL & GO TO DASHBOARD
      </Button>
    </div>
  );
};

export default PaymentSection;
